// CustomFieldsSection — entity tipinin dinamik alanlarını form içinde render eder.
// Kullanım (içe gömülü, otomatik kayıt yapmaz — values'u parent yönetir):
//   <CustomFieldsSection
//     entityType="account"
//     values={form.custom_fields || {}}
//     onChange={(newValues) => setForm({...form, custom_fields: newValues})}
//     disabled={!isEditing}
//   />
//
// Veya auto-save modunda (record kaydedildikten sonra kullanım):
//   <CustomFieldsSection entityType="account" entityId={id} autoSave />
//
// Boş entity_type ise render etmez.
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { toast } from './Toast';
import { FormField } from './ui';

export default function CustomFieldsSection({
  entityType,
  entityId,
  values: extValues,
  onChange,
  disabled = false,
  autoSave = false,
  title = 'Özel Alanlar',
  emptyMessage = 'Bu modül için henüz özel alan tanımlanmamış.',
}) {
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localValues, setLocalValues] = useState({});
  const [saving, setSaving] = useState(false);

  // Tanımları çek
  useEffect(() => {
    if (!entityType) return;
    let cancelled = false;
    setLoading(true);
    api.get('/custom-fields/definitions', { params: { entity_type: entityType } })
      .then(r => { if (!cancelled && r.data.success) setDefs(r.data.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [entityType]);

  // autoSave modunda values'i backend'ten çek
  useEffect(() => {
    if (!autoSave || !entityType || !entityId) return;
    let cancelled = false;
    api.get('/custom-fields/values', { params: { entity_type: entityType, id: entityId } })
      .then(r => { if (!cancelled && r.data.success) setLocalValues(r.data.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [autoSave, entityType, entityId]);

  // External values değişirse senkron tut
  useEffect(() => {
    if (!autoSave && extValues) setLocalValues(extValues || {});
  }, [extValues, autoSave]);

  const setValue = useCallback((key, val) => {
    setLocalValues(prev => {
      const next = { ...prev, [key]: val };
      if (val === null || val === undefined || val === '') delete next[key];
      if (onChange) onChange(next);
      return next;
    });
  }, [onChange]);

  // autoSave modunda blur'da kaydet
  const handleBlurAutoSave = useCallback(async () => {
    if (!autoSave || !entityType || !entityId) return;
    setSaving(true);
    try {
      await api.put('/custom-fields/values', {
        entity_type: entityType,
        entity_id: entityId,
        values: localValues,
        replace: true,
      });
    } catch (e) {
      toast.error('Özel alanlar kaydedilemedi');
    } finally { setSaving(false); }
  }, [autoSave, entityType, entityId, localValues]);

  if (loading) {
    return (
      <div style={{ padding: 16, color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
        Özel alanlar yükleniyor...
      </div>
    );
  }

  if (!defs.length) {
    return (
      <div style={S.empty}>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginBottom: 6 }}>{emptyMessage}</div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
          Program Tanımları → Özel Alanlar bölümünden ekleyebilirsiniz.
        </div>
      </div>
    );
  }

  // Gruplara göre ayır
  const groups = {};
  for (const d of defs) {
    const g = d.group_label || '';
    if (!groups[g]) groups[g] = [];
    groups[g].push(d);
  }
  const groupKeys = Object.keys(groups).sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b, 'tr')));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {title && <div style={S.title}>{title}{saving && <span style={S.saving}> kaydediliyor...</span>}</div>}
      {groupKeys.map(gKey => (
        <div key={gKey}>
          {gKey && <div style={S.groupTitle}>{gKey}</div>}
          <div className="form-grid">
            {groups[gKey].map(def => (
              <FieldRenderer
                key={def.id}
                def={def}
                value={localValues[def.field_key]}
                onChange={(v) => setValue(def.field_key, v)}
                onBlur={handleBlurAutoSave}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FieldRenderer({ def, value, onChange, onBlur, disabled }) {
  const required = !!def.is_required;
  const ph = def.placeholder || '';
  const help = def.help_text || '';
  let input;

  switch (def.field_type) {
    case 'text':
      input = <input type="text" value={value ?? ''} placeholder={ph} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case 'textarea':
      input = <textarea rows={3} value={value ?? ''} placeholder={ph} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case 'number':
      input = <input type="number" step="1" value={value ?? ''} placeholder={ph} disabled={disabled} onChange={e => onChange(e.target.value === '' ? null : parseInt(e.target.value))} onBlur={onBlur} />;
      break;
    case 'decimal':
      input = <input type="number" step="0.01" value={value ?? ''} placeholder={ph} disabled={disabled} onChange={e => onChange(e.target.value === '' ? null : parseFloat(e.target.value))} onBlur={onBlur} />;
      break;
    case 'date':
      input = <input type="date" value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case 'datetime':
      input = <input type="datetime-local" value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case 'boolean':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
          <input type="checkbox" id={'cf-' + def.field_key} checked={!!value} disabled={disabled}
                 onChange={e => { onChange(e.target.checked); if (onBlur) setTimeout(onBlur, 0); }}
                 style={{ width: 18, height: 18, accentColor: 'var(--accent-primary)' }} />
          <label htmlFor={'cf-' + def.field_key} style={{ fontSize: 'var(--text-sm)', cursor: 'pointer' }}>
            {def.label}{required && <span style={{ color: 'var(--danger)' }}> *</span>}
          </label>
          {help && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', marginLeft: 'auto' }}>{help}</span>}
        </div>
      );
    case 'select': {
      const opts = parseOptions(def.options_json);
      input = (
        <select value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value || null)} onBlur={onBlur}>
          <option value="">— Seçiniz —</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
      break;
    }
    case 'multiselect': {
      const opts = parseOptions(def.options_json);
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      input = (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {opts.map(o => {
            const checked = arr.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                disabled={disabled}
                onClick={() => {
                  const next = checked ? arr.filter(x => x !== o.value) : [...arr, o.value];
                  onChange(next.length ? next : null);
                  if (onBlur) setTimeout(onBlur, 0);
                }}
                style={{
                  padding: '4px 10px',
                  fontSize: 'var(--text-xs)',
                  border: '1px solid ' + (checked ? 'var(--accent-primary)' : 'var(--border-input)'),
                  background: checked ? 'var(--accent-glow)' : 'var(--bg-card)',
                  color: checked ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  borderRadius: 'var(--r-full)',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {checked ? '✓ ' : ''}{o.label}
              </button>
            );
          })}
        </div>
      );
      break;
    }
    case 'url':
      input = <input type="url" value={value ?? ''} placeholder={ph || 'https://...'} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case 'email':
      input = <input type="email" value={value ?? ''} placeholder={ph} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    case 'phone':
      input = <input type="tel" value={value ?? ''} placeholder={ph} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
      break;
    default:
      input = <input type="text" value={value ?? ''} disabled={disabled} onChange={e => onChange(e.target.value)} onBlur={onBlur} />;
  }

  return (
    <FormField label={def.label} required={required} hint={help}
               fullWidth={def.field_type === 'textarea' || def.field_type === 'multiselect'}>
      {input}
    </FormField>
  );
}

function parseOptions(opts) {
  if (!opts) return [];
  try {
    if (typeof opts === 'string') opts = JSON.parse(opts);
    if (!Array.isArray(opts)) return [];
    return opts.map(o => typeof o === 'string' ? { value: o, label: o } : { value: o.value, label: o.label || o.value });
  } catch { return []; }
}

const S = {
  title: {
    fontSize: 'var(--text-sm)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    paddingBottom: 6,
    borderBottom: '1px solid var(--border-secondary)',
  },
  saving: { fontSize: 'var(--text-xs)', fontWeight: 400, color: 'var(--text-tertiary)' },
  groupTitle: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 6,
  },
  empty: {
    padding: '20px 16px',
    textAlign: 'center',
    background: 'var(--bg-card-hover)',
    borderRadius: 'var(--r-md)',
    border: '1px dashed var(--border-secondary)',
  },
};
